import React, { useState, useEffect, useCallback } from 'react';
import {
  IconPlus, IconEdit, IconTrash, IconToggleLeft, IconToggleRight,
  IconBell, IconAlertTriangle, IconRefresh,
} from '@tabler/icons-react';
import api from '../api/api';
import { onBackdropMouseDown, onBackdropClick } from '../utils/modalDrag';

function formatDate(str) {
  if (!str) return '-';
  const d = new Date(str);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toInputDatetime(str) {
  if (!str) return '';
  return str.slice(0, 16); // yyyy-MM-ddTHH:mm
}

const EMPTY_FORM = {
  type: 'BANNER',
  title: '',
  content: '',
  linkUrl: '',
  bgColor: '#e7f3ff',
  textColor: '#0d6efd',
  startDate: '',
  endDate: '',
  isActive: true,
  dismissible: true,
};

export default function NotificationPage({ me, showToast }) {
  const [notifications, setNotifications] = useState([]);
  const [page, setPage]             = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading]       = useState(false);

  // 모달
  const [modalOpen, setModalOpen]   = useState(false);
  const [editId, setEditId]         = useState(null);
  const [form, setForm]             = useState({ ...EMPTY_FORM });
  const [saving, setSaving]         = useState(false);

  const fetchNotifications = useCallback(async (p = 0) => {
    setLoading(true);
    try {
      const res = await api.get(`/notifications?page=${p}&size=20`);
      const data = res.data;
      setNotifications(data.content || []);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
      setPage(p);
    } catch (err) {
      showToast('알림 목록을 불러올 수 없습니다.', 'danger');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchNotifications(0); }, [fetchNotifications]);

  const openCreate = () => {
    setEditId(null);
    setForm({ ...EMPTY_FORM });
    setModalOpen(true);
  };

  const openEdit = (n) => {
    setEditId(n.id);
    setForm({
      type: n.type,
      title: n.title || '',
      content: n.content || '',
      linkUrl: n.linkUrl || '',
      bgColor: n.bgColor || '#e7f3ff',
      textColor: n.textColor || '#0d6efd',
      startDate: toInputDatetime(n.startDate),
      endDate: toInputDatetime(n.endDate),
      isActive: n.isActive,
      dismissible: n.dismissible,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      showToast('제목을 입력해주세요.', 'warning');
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
      };
      if (editId) {
        await api.put(`/notifications/${editId}`, body);
        showToast('알림이 수정되었습니다.', 'success');
      } else {
        await api.post('/notifications', body);
        showToast('알림이 등록되었습니다.', 'success');
      }
      setModalOpen(false);
      fetchNotifications(page);
    } catch (err) {
      showToast('저장 실패: ' + (err.response?.data?.error || err.message), 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (n) => {
    try {
      await api.patch(`/notifications/${n.id}/toggle`);
      fetchNotifications(page);
      showToast(`알림이 ${n.isActive ? '비활성화' : '활성화'}되었습니다.`, 'success');
    } catch (err) {
      showToast('변경 실패', 'danger');
    }
  };

  const handleDelete = async (n) => {
    if (!window.confirm(`"${n.title}" 알림을 삭제하시겠습니까?`)) return;
    try {
      await api.delete(`/notifications/${n.id}`);
      showToast('알림이 삭제되었습니다.', 'success');
      fetchNotifications(page);
    } catch (err) {
      showToast('삭제 실패', 'danger');
    }
  };

  const isMobile = () => window.innerWidth <= 768;

  return (
    <div className="container-xl">
      <div className="page-header d-print-none mb-3">
        <div className="row align-items-center">
          <div className="col">
            <h2 className="page-title">공지/알림 관리</h2>
            <div className="text-muted mt-1">총 {totalElements}건</div>
          </div>
          <div className="col-auto">
            <button className="btn btn-primary btn-sm" onClick={openCreate}>
              <IconPlus size={16} className="me-1" />새 알림
            </button>
          </div>
        </div>
      </div>

      {/* 알림 목록 테이블 */}
      <div className="card">
        <div className="table-responsive">
          <table className="table table-vcenter card-table table-striped">
            <thead>
              <tr>
                <th style={{ whiteSpace: 'nowrap' }}>유형</th>
                <th>제목</th>
                <th style={{ whiteSpace: 'nowrap' }}>상태</th>
                <th style={{ whiteSpace: 'nowrap' }}>노출기간</th>
                <th style={{ whiteSpace: 'nowrap' }}>작성자</th>
                <th style={{ whiteSpace: 'nowrap' }}>등록일</th>
                <th style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>관리</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-4">로딩 중...</td></tr>
              ) : notifications.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-4 text-muted">등록된 알림이 없습니다.</td></tr>
              ) : notifications.map(n => (
                <tr key={n.id}>
                  <td>
                    <span className={`badge text-white ${n.type === 'BANNER' ? 'bg-blue' : 'bg-purple'}`}>
                      {n.type === 'BANNER' ? '배너' : '팝업'}
                    </span>
                  </td>
                  <td style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {n.title}
                  </td>
                  <td>
                    <span className={`badge text-white ${n.isActive ? 'bg-success' : 'bg-secondary'}`}>
                      {n.isActive ? '활성' : '비활성'}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                    {n.startDate || n.endDate
                      ? `${formatDate(n.startDate)} ~ ${formatDate(n.endDate)}`
                      : '상시'}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{n.createdByName || '-'}</td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>{formatDate(n.createdAt)}</td>
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                    <div className="d-flex gap-1 justify-content-center">
                      <button className="btn btn-sm btn-ghost-primary" title="수정" onClick={() => openEdit(n)}>
                        <IconEdit size={16} />
                      </button>
                      <button className="btn btn-sm btn-ghost-warning" title={n.isActive ? '비활성화' : '활성화'}
                        onClick={() => handleToggle(n)}>
                        {n.isActive ? <IconToggleRight size={16} /> : <IconToggleLeft size={16} />}
                      </button>
                      <button className="btn btn-sm btn-ghost-danger" title="삭제" onClick={() => handleDelete(n)}>
                        <IconTrash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 페이징 */}
        {totalPages > 1 && (
          <div className="card-footer d-flex justify-content-center">
            <ul className="pagination mb-0">
              <li className={`page-item ${page === 0 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => fetchNotifications(page - 1)}>이전</button>
              </li>
              {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                const startP = Math.max(0, Math.min(page - 4, totalPages - 10));
                const p = startP + i;
                if (p >= totalPages) return null;
                return (
                  <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => fetchNotifications(p)}>{p + 1}</button>
                  </li>
                );
              })}
              <li className={`page-item ${page >= totalPages - 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => fetchNotifications(page + 1)}>다음</button>
              </li>
            </ul>
          </div>
        )}
      </div>

      {/* 등록/수정 모달 */}
      {modalOpen && (
        <>
          <div className="modal-backdrop fade show" />
          <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex={-1}
            onMouseDown={onBackdropMouseDown} onClick={(e) => onBackdropClick(e, () => setModalOpen(false))}>
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editId ? '알림 수정' : '새 알림 등록'}</h5>
                  <button type="button" className="btn-close" onClick={() => setModalOpen(false)} />
                </div>
                <div className="modal-body">
                  {/* 유형 */}
                  <div className="mb-3">
                    <label className="form-label">유형 <span className="text-danger">*</span></label>
                    <div className="d-flex gap-3">
                      <label className="form-check form-check-inline">
                        <input className="form-check-input" type="radio" name="type"
                          checked={form.type === 'BANNER'} onChange={() => setForm(f => ({ ...f, type: 'BANNER' }))} />
                        <span className="form-check-label">
                          <IconBell size={16} className="me-1" />배너 (상단 띠)
                        </span>
                      </label>
                      <label className="form-check form-check-inline">
                        <input className="form-check-input" type="radio" name="type"
                          checked={form.type === 'POPUP'} onChange={() => setForm(f => ({ ...f, type: 'POPUP' }))} />
                        <span className="form-check-label">
                          <IconAlertTriangle size={16} className="me-1" />팝업
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* 제목 */}
                  <div className="mb-3">
                    <label className="form-label">제목 <span className="text-danger">*</span></label>
                    <input type="text" className="form-control form-control-sm" maxLength={200}
                      value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
                  </div>

                  {/* 내용 */}
                  <div className="mb-3">
                    <label className="form-label">내용</label>
                    <textarea className="form-control form-control-sm" rows={4}
                      value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} />
                  </div>

                  {/* 링크 URL */}
                  <div className="mb-3">
                    <label className="form-label">링크 URL (선택)</label>
                    <input type="text" className="form-control form-control-sm" placeholder="https://..."
                      value={form.linkUrl} onChange={e => setForm(f => ({ ...f, linkUrl: e.target.value }))} />
                  </div>

                  {/* 배너 스타일 - BANNER만 */}
                  {form.type === 'BANNER' && (
                    <div className="row mb-3">
                      <div className="col-6">
                        <label className="form-label">배경색</label>
                        <div className="d-flex align-items-center gap-2">
                          <input type="color" className="form-control form-control-color" style={{ width: 40, height: 32, padding: 2 }}
                            value={form.bgColor} onChange={e => setForm(f => ({ ...f, bgColor: e.target.value }))} />
                          <input type="text" className="form-control form-control-sm" style={{ width: 100 }}
                            value={form.bgColor} onChange={e => setForm(f => ({ ...f, bgColor: e.target.value }))} />
                        </div>
                      </div>
                      <div className="col-6">
                        <label className="form-label">글자색</label>
                        <div className="d-flex align-items-center gap-2">
                          <input type="color" className="form-control form-control-color" style={{ width: 40, height: 32, padding: 2 }}
                            value={form.textColor} onChange={e => setForm(f => ({ ...f, textColor: e.target.value }))} />
                          <input type="text" className="form-control form-control-sm" style={{ width: 100 }}
                            value={form.textColor} onChange={e => setForm(f => ({ ...f, textColor: e.target.value }))} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 배너 미리보기 */}
                  {form.type === 'BANNER' && form.title && (
                    <div className="mb-3">
                      <label className="form-label">미리보기</label>
                      <div style={{
                        backgroundColor: form.bgColor, color: form.textColor,
                        padding: '10px 16px', borderRadius: 6, fontSize: 14, fontWeight: 500,
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}>
                        <IconBell size={16} />
                        <span>{form.title}</span>
                      </div>
                    </div>
                  )}

                  {/* 노출 기간 */}
                  <div className="row mb-3">
                    <div className="col-6">
                      <label className="form-label">노출 시작</label>
                      <input type="datetime-local" className="form-control form-control-sm"
                        value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} />
                    </div>
                    <div className="col-6">
                      <label className="form-label">노출 종료</label>
                      <input type="datetime-local" className="form-control form-control-sm"
                        value={form.endDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} />
                    </div>
                    <div className="col-12 mt-1">
                      <small className="text-muted">비워두면 상시 노출됩니다.</small>
                    </div>
                  </div>

                  {/* 옵션 */}
                  <div className="d-flex gap-4 mb-3">
                    <label className="form-check">
                      <input className="form-check-input" type="checkbox"
                        checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
                      <span className="form-check-label">활성화</span>
                    </label>
                    {form.type === 'POPUP' && (
                      <label className="form-check">
                        <input className="form-check-input" type="checkbox"
                          checked={form.dismissible} onChange={e => setForm(f => ({ ...f, dismissible: e.target.checked }))} />
                        <span className="form-check-label">오늘 하루 안 보기 허용</span>
                      </label>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-sm btn-secondary" onClick={() => setModalOpen(false)}>취소</button>
                  <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}>
                    {saving ? '저장 중...' : (editId ? '수정' : '등록')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
